//updated WebRTCService.ts
// src/services/webRTCService.ts
// This service handles peer-to-peer connections using WebRTC

import DOMPurify from "dompurify";
import { db } from "./firebaseService";
import {
  collection,
  doc,
  setDoc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  deleteDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";


export type ImageCallback = (blob: Blob, from: string) => void;

// WebRTC configuration with STUN servers for NAT traversal
const rtcConfig = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

export interface PeerMessage {
  type:
    | "progress"
    | "complete"
    | "elementUnlocked"
    | "ping"
    | "start"
    | "chat"
    | "move";
  data: any;
  timestamp: number;
}

class WebRTCService {
  private peerConnection: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private userId: string | null = null;
  private sessionId: string | null = null;
  private isInitiator: boolean = false;
  private messageListeners: ((message: PeerMessage) => void)[] = [];
  private connectionStateListeners: ((
    state: RTCPeerConnectionState
  ) => void)[] = [];
  private unsubscribeFunctions: (() => void)[] = [];

  private imageCallback: ImageCallback | null = null;

  /** Call from your app once to register a handler for incoming images */
  public onImageReceived(cb: ImageCallback) {
    this.imageCallback = cb;
  }

  /**
   * Send a File over the opened RTC data channel as raw bytes.
   * We sanitize the filename before displaying it.
   */
  public async sendImage(file: File) {
    if (!this.dataChannel || this.dataChannel.readyState !== "open") {
      throw new Error("Data channel not open");
    }
    // sanitize filename (to prevent XSS if you ever show it)
    const safeName = DOMPurify.sanitize(file.name, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });

    // read binary
    const buffer = await file.arrayBuffer();

    // prefix a tiny JSON header so the receiver knows it's an image
    const header = JSON.stringify({
      type: "image",
      name: safeName,
      size: buffer.byteLength,
    });
    const headerBytes = new TextEncoder().encode(header + "\n");

    // send header
    this.dataChannel.send(headerBytes);

    // send raw bytes
    this.dataChannel.send(buffer);
  }

  init(userId: string) {
    this.userId = userId;
    console.log("WebRTC service initialized for user:", userId);
  }

  cleanup() {
    console.log("Cleaning up WebRTC connections");
    this.closeConnection();
    this.unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
    this.unsubscribeFunctions = [];
    this.sessionId = null;
    this.isInitiator = false;
  }

  closeConnection() {
    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
  }

  async createBattleSession(sessionDetails: any): Promise<string> {
    if (!this.userId)
      throw new Error("WebRTC service not initialized with user ID");

    this.isInitiator = true;
    const sessionRef = doc(collection(db, "webrtcSessions"));
    this.sessionId = sessionRef.id;

    await setDoc(sessionRef, {
      id: this.sessionId,
      initiatorId: this.userId,
      status: "waiting",
      created: serverTimestamp(),
      gameDetails: sessionDetails,
    });

    this.createPeerConnection();
    this.dataChannel = this.peerConnection!.createDataChannel("gameData");
    this.setupDataChannel(this.dataChannel);

    const offer = await this.peerConnection!.createOffer();
    await this.peerConnection!.setLocalDescription(offer);

    await updateDoc(sessionRef, {
      offer: { type: offer.type, sdp: offer.sdp },
      lastUpdated: serverTimestamp(),
    });

    this.listenForSessionUpdates(this.sessionId);
    return this.sessionId;
  }

  async joinBattleSession(sessionId: string): Promise<any> {
    if (!this.userId)
      throw new Error("WebRTC service not initialized with user ID");

    this.isInitiator = false;
    this.sessionId = sessionId;

    const sessionDoc = await getDocs(
      query(collection(db, "webrtcSessions"), where("id", "==", sessionId))
    );
    if (sessionDoc.empty) throw new Error("Session not found");

    const sessionData = sessionDoc.docs[0].data();
    this.createPeerConnection();

    this.peerConnection!.ondatachannel = (event) => {
      this.dataChannel = event.channel;
      this.setupDataChannel(this.dataChannel);
    };

    if (sessionData.offer) {
      const remoteDesc = new RTCSessionDescription(sessionData.offer);
      await this.peerConnection!.setRemoteDescription(remoteDesc);
      const answer = await this.peerConnection!.createAnswer();
      await this.peerConnection!.setLocalDescription(answer);
      await updateDoc(doc(db, "webrtcSessions", sessionId), {
        answer: { type: answer.type, sdp: answer.sdp },
        participantId: this.userId,
        status: "connecting",
        lastUpdated: serverTimestamp(),
      });
    } else {
      throw new Error("No offer found in session");
    }

    this.listenForSessionUpdates(sessionId);
    return sessionData.gameDetails;
  }

  async findAvailableSessions(): Promise<any[]> {
    const snapshot = await getDocs(
      query(collection(db, "webrtcSessions"), where("status", "==", "waiting"))
    );
    const sessions: any[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.initiatorId !== this.userId) sessions.push(data);
    });
    return sessions;
  }

  async endSession(winner: string | null = null): Promise<void> {
    if (!this.sessionId) return;
    await updateDoc(doc(db, "webrtcSessions", this.sessionId), {
      status: "completed",
      winner,
      completedAt: serverTimestamp(),
    });
    this.cleanup();
  }

  sendMessage(type: PeerMessage["type"], data: any) {
    if (!this.dataChannel || this.dataChannel.readyState !== "open") {
      console.warn("Data channel not ready, can't send message");
      return false;
    }
    try {
      const message: PeerMessage = { type, data, timestamp: Date.now() };
      this.dataChannel.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error("Error sending message:", error);
      return false;
    }
  }

  addMessageListener(callback: (message: PeerMessage) => void) {
    this.messageListeners.push(callback);
  }

  removeMessageListener(callback: (message: PeerMessage) => void) {
    this.messageListeners = this.messageListeners.filter(
      (listener) => listener !== callback
    );
  }

  addConnectionStateListener(
    callback: (state: RTCPeerConnectionState) => void
  ) {
    this.connectionStateListeners.push(callback);
  }

  removeConnectionStateListener(
    callback: (state: RTCPeerConnectionState) => void
  ) {
    this.connectionStateListeners = this.connectionStateListeners.filter(
      (listener) => listener !== callback
    );
  }

  isConnected(): boolean {
    return !!(
      this.peerConnection &&
      this.dataChannel &&
      this.dataChannel.readyState === "open" &&
      this.peerConnection.connectionState === "connected"
    );
  }

  private createPeerConnection() {
    if (this.peerConnection) this.closeConnection();

    this.peerConnection = new RTCPeerConnection(rtcConfig);

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.sessionId) {
        const iceCandidatesCollection = collection(
          db,
          "webrtcSessions",
          this.sessionId,
          this.isInitiator ? "initiatorCandidates" : "participantCandidates"
        );
        setDoc(doc(iceCandidatesCollection), {
          candidate: event.candidate.candidate,
          sdpMid: event.candidate.sdpMid,
          sdpMLineIndex: event.candidate.sdpMLineIndex,
          timestamp: serverTimestamp(),
        }).catch(console.error);
      }
    };

    this.peerConnection.onconnectionstatechange = () => {
      if (this.peerConnection) {
        this.connectionStateListeners.forEach((listener) =>
          listener(this.peerConnection!.connectionState)
        );
        if (this.sessionId) {
          const statusUpdate =
            this.peerConnection.connectionState === "connected"
              ? { status: "active", connectedAt: serverTimestamp() }
              : ["disconnected", "failed", "closed"].includes(
                  this.peerConnection.connectionState
                )
              ? { status: "disconnected", disconnectedAt: serverTimestamp() }
              : null;
          if (statusUpdate) {
            updateDoc(
              doc(db, "webrtcSessions", this.sessionId),
              statusUpdate
            ).catch(console.error);
          }
        }
      }
    };
  }

  private setupDataChannel(channel: RTCDataChannel) {
    channel.onopen = () => {
      const pingInterval = setInterval(() => {
        if (channel.readyState === "open") {
          this.sendMessage("ping", { timestamp: Date.now() });
        } else {
          clearInterval(pingInterval);
        }
      }, 15000);

      channel.onclose = () => clearInterval(pingInterval);
    };

    channel.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as PeerMessage;
        this.messageListeners.forEach((listener) => listener(message));
      } catch (error) {
        console.error("Error parsing message:", error);
      }
    };

    channel.onerror = (error) => console.error("Data channel error:", error);

    channel.onmessage = async (evt) => {
      // distinguish text vs binary
      if (typeof evt.data === "string") {
        // your existing JSON‐chat handler
        const msg = JSON.parse(evt.data) as PeerMessage;
        this.messageListeners.forEach((l) => l(msg));
      } else {
        // binary => first read until newline for header
        const buf = await evt.data.arrayBuffer();
        // naive split: header until first \n
        const view = new Uint8Array(buf);
        const sep = view.indexOf(10); // '\n'
        if (sep > 0) {
          const headerText = new TextDecoder().decode(view.subarray(0, sep));
          const { type, name } = JSON.parse(headerText);
          if (type === "image") {
            const imageBytes = buf.slice(sep + 1);
            const blob = new Blob([imageBytes]);
            // fire your callback
            this.imageCallback?.(blob, this.userId!);
          }
        }
      }
    };
  }

  private listenForSessionUpdates(sessionId: string) {
    const sessionUnsubscribe = onSnapshot(
      doc(db, "webrtcSessions", sessionId),
      async (snapshot) => {
        if (!snapshot.exists()) return;
        const data = snapshot.data();
        if (
          this.isInitiator &&
          data.answer &&
          this.peerConnection?.remoteDescription === null
        ) {
          try {
            const remoteDesc = new RTCSessionDescription(data.answer);
            await this.peerConnection?.setRemoteDescription(remoteDesc);
          } catch (error) {
            console.error("Error setting remote description:", error);
          }
        }
      },
      console.error
    );

    this.unsubscribeFunctions.push(sessionUnsubscribe);

    const candidatesCollection = collection(
      db,
      "webrtcSessions",
      sessionId,
      this.isInitiator ? "participantCandidates" : "initiatorCandidates"
    );

    const candidatesUnsubscribe = onSnapshot(
      candidatesCollection,
      (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === "added") {
            const data = change.doc.data();
            if (this.peerConnection?.remoteDescription) {
              try {
                await this.peerConnection.addIceCandidate(
                  new RTCIceCandidate({
                    candidate: data.candidate,
                    sdpMid: data.sdpMid,
                    sdpMLineIndex: data.sdpMLineIndex,
                  })
                );
              } catch (error) {
                console.error("Error adding ICE candidate:", error);
              }
            }
          }
        });
      },
      console.error
    );

    this.unsubscribeFunctions.push(candidatesUnsubscribe);
  }
}

export const webRTCService = new WebRTCService();
